// 新聞頁面 - 留言輸入區塊
import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { useDispatch, useSelector } from 'react-redux';
import { userState } from '@/slices/userSlice';
import _ from 'lodash';
import {
    setHintText,
    setShowHintModal,
    setShowLoginModal,
    setShowComment,
    popState,
} from '@/slices/popSlice';
import { setUrlQuery } from '@/slices/urlSlice';
import Image from 'next/image';
import { DEFAULT_USER_IMG } from '@/common/constant';
import newsClient from '@/common/apiClient/newsClient';

import NewsCommentConfirm from './NewsCommentConfirm'; // 留言確認彈窗
import NewsForbidComment from './NewsForbidComment'; // 禁言彈窗
import NewsCommentInputComponent from './NewsCommentInputComponent'; // 輸入匡元件

const NewsCommentInput = ({ t, newsId, targetRef }) => {
    const dispatch = useDispatch();
    const router = useRouter();
    const { user } = useSelector(userState);
    const { showComment } = useSelector(popState);
    const inputRef = useRef(null);
    const searchParams = useSearchParams();
    const scrollToComment = searchParams.get('scrollToComment');

    // 留言內容
    const [comment, setComment] = useState('');
    const [placeholder, setPlaceholder] = useState(t('COMMENT'));

    // 留言確認彈窗
    const [showConfirm, setShowConfirm] = useState(false);
    // 禁言彈窗
    const [showForbid, setShowForbid] = useState(false);

    // 點擊留言
    const onSubmit = () => {
        if (comment.trim() === '') return;

        // 需登入
        if (!user?.uid) {
            dispatch(setShowLoginModal(true));
            return;
        }

        // 如果被禁言
        if (user?.muted) {
            setShowConfirm(false);
            setShowForbid(true);
            return;
        }

        setShowConfirm(true);
    };

    // 處理提示彈窗
    const handleHint = (text) => {
        setShowConfirm(false);
        dispatch(setHintText(text));
        dispatch(setShowHintModal(true));
    };

    // 跳到留言
    const handleJumpToComment = () => {
        const target = document.querySelector('#comment-target');
        if (target) target.scrollIntoView({ block: 'center' });
    };

    // 送出留言
    const onComment = async () => {
        try {
            const result = await newsClient.postNewsComment(newsId, comment);
            if (result?.message === 'success') {
                handleHint('HINT_COMMENT');
                setComment('');
                dispatch(setShowComment(false));
                handleJumpToComment();
            }
        } catch (e) {
            // 禁字提示
            if (e.message === 'banned words') handleHint('HINT_INAPPROPRIATE');
            console.error(e);
        }
    };

    // focus input
    const onFocus = () => {
        if (!user?.uid) {
            inputRef.current.blur();
            dispatch(setUrlQuery(`?scrollToComment=true`));
            dispatch(setShowLoginModal(true));
            return;
        }
        setPlaceholder('');
    };

    // blur input
    const onBlur = () => {
        if (comment.trim() === '') setPlaceholder(t('COMMENT'));
    };

    // 防連點
    const commentDebounce = useMemo(() => _.debounce(onComment, 300), [onComment]);

    const handleScrollToComment = () => {
        setTimeout(() => {
            if (window.innerWidth > 1023) targetRef.current.scrollIntoView();
            else dispatch(setShowComment(true));
        }, 200);
        dispatch(setUrlQuery(''));
        router.replace(`${window.location.pathname}`);
    };

    useEffect(() => {
        return () => commentDebounce.cancel();
    }, [commentDebounce]);

    useEffect(() => {
        if (scrollToComment) handleScrollToComment();
    }, [scrollToComment]);

    return (
        <>
            {/* 手機輸入筐 */}
            <NewsCommentInputComponent
                type={'modal'}
                showComment={showComment}
                user={user}
                comment={comment}
                setComment={setComment}
                onFocus={onFocus}
                onBlur={onBlur}
                inputRef={inputRef}
                placeholder={placeholder}
                onSubmit={onSubmit}
                t={t}
            />
            {/* 桌機輸入筐 */}
            <NewsCommentInputComponent
                type={'inline'}
                showComment={showComment}
                user={user}
                comment={comment}
                setComment={setComment}
                onFocus={onFocus}
                onBlur={onBlur}
                inputRef={inputRef}
                placeholder={placeholder}
                onSubmit={onSubmit}
                t={t}
            />

            {/* 確認留言彈窗 */}
            {showConfirm && (
                <NewsCommentConfirm
                    onComment={commentDebounce}
                    t={t}
                    setShowConfirm={setShowConfirm}
                />
            )}

            {/* 禁言彈窗 */}
            {showForbid && (
                <NewsForbidComment muted={user?.muted} t={t} setShowForbid={setShowForbid} />
            )}

            {/* 關閉用背景 */}
            {showComment && (
                <div
                    className="fixed top-0 left-0 w-full h-full z-[3]"
                    onClick={() => dispatch(setShowComment(false))}
                />
            )}
        </>
    );
};

export default NewsCommentInput;
